import React, { useState, useEffect } from 'react';
import { Segment } from '../types';
import { api } from '../utils/api';

interface SegmentsListProps {
  onSegmentSelected?: (segment: Segment) => void;
}

const SegmentsList: React.FC<SegmentsListProps> = ({ onSegmentSelected }) => {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedSegments = await api.getSegments();
      setSegments(fetchedSegments);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch segments');
    } finally {
      setLoading(false);
    }
  };

  const handleSegmentClick = (segment: Segment) => {
    if (onSegmentSelected) {
      onSegmentSelected(segment);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow p-6">
        <div className="h-64 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <div className="text-lg mt-4 text-gray-600 dark:text-gray-300">Loading segments...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow p-6">
        <div className="h-64 flex items-center justify-center">
          <div className="text-red-600 text-lg">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold text-gray-900">Segments</h2>
        <p className="text-gray-600 text-sm mt-1">
          {segments.length} segment{segments.length !== 1 ? 's' : ''} found
        </p>
      </div>
      
      <div className="max-h-96 overflow-y-auto">
        {segments.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No segments found. Create your first segment!
          </div>
        ) : (
          segments.map((segment) => (
            <div
              key={segment.id}
              className="p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => handleSegmentClick(segment)}
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-medium text-gray-900">{segment.name}</h3>
                <span className="text-xs text-gray-500">ID: {segment.id}</span>
              </div>
              
              <div className="text-sm text-gray-600 space-y-1">
                <div>
                  <span className="font-medium">Start:</span> {segment.start_lat.toFixed(6)}, {segment.start_lon.toFixed(6)}
                </div>
                <div>
                  <span className="font-medium">End:</span> {segment.end_lat.toFixed(6)}, {segment.end_lon.toFixed(6)}
                </div>
                <div className="flex gap-4 text-xs">
                  <span>Radius Start: {segment.radius_start || 50}m</span>
                  <span>Radius End: {segment.radius_end || 50}m</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SegmentsList;
